import { Button, Fade, Stack, Typography } from "@mui/material";
import { Issue } from "generated-graphql/graphql";
import pluralize from "pluralize";
import { useMemo, useState } from "react";

import { ControlledBigTooltip } from "./ControlledBigTooltip";

interface SectionComplianceIssuesButtonProps {
  issues: Issue[];
  sectionHeader: string;
}

export const SectionComplianceIssuesButton: React.FC<SectionComplianceIssuesButtonProps> = ({
  issues,
  sectionHeader,
}) => {
  const [open, setOpen] = useState(false);

  const countLabel = useMemo(() => {
    const count = issues.length;
    return `${count} compliance ${pluralize("issue", count)} in this section`;
  }, [issues.length]);

  const buttonLabel = useMemo(() => {
    const count = issues.length;
    return `${count > 0 ? count : "No"} Compliance ${pluralize("Issue", count)}`;
  }, [issues.length]);

  return (
    <ControlledBigTooltip
      open={open}
      onClose={() => setOpen(false)}
      placement="bottom-end"
      slots={{ transition: Fade }}
      slotProps={{ tooltip: { sx: { maxWidth: (theme) => theme.spacing(65) } } }}
      title={
        <Stack
          gap={1}
          sx={{
            marginBottom: 0.5,
            maxHeight: (theme) => theme.spacing(75),
            overflowY: "auto",
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} fontSize="12px">
            {countLabel}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} fontSize="12px">
            {sectionHeader}
          </Typography>
          <Typography
            component="ul"
            sx={{
              m: 0,
              pl: 2,
              fontSize: "12px",
            }}
          >
            {issues.map((issue, index) => (
              <li key={`${issue.modelName}-${issue.key ?? "none"}-${index}`}>{issue.message}</li>
            ))}
          </Typography>
        </Stack>
      }
    >
      <Button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        disabled={issues.length === 0}
        variant="text"
        size="small"
        sx={{ color: "text.secondary", fontSize: "0.75rem" }}
      >
        {buttonLabel}
      </Button>
    </ControlledBigTooltip>
  );
};
